/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from "react";
import { Card } from "primereact/card";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { Skeleton } from "primereact/skeleton";
import { Menu } from "primereact/menu";
import { confirmDialog, ConfirmDialog } from "primereact/confirmdialog";
import {
  Boton,
  Titulo,
  Formulario,
  accessAPI,
  Sidebar,
  tituloBase,
  TablaFiltrable
} from "../../../../shared";

const SEARCH_FIELDS = ["nombre"];

export default function DocumentoCarpetas() {
  const [loader, setLoader] = useState(true);
  const [carpetas, setCarpetas] = useState([]);

  const [sideBarCrear, setSideBarCrear] = useState(false);
  const [loaderCrear, setLoaderCrear] = useState(false);
  const [formCrearKey, setFormCrearKey] = useState(0);

  const [sideBarEditar, setSideBarEditar] = useState(false);
  const [loaderEditar, setLoaderEditar] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formEditarKey, setFormEditarKey] = useState(0);

  const [menuSeleccionada, setMenuSeleccionada] = useState(null);
  const menuRef = useRef(null);

  const toast = useRef(null);

  const crearFormConfig = {
    secciones: [
      {
        campos: [
          {
            nombre: "nombre",
            tipoitemformularioid: 1,
            etiqueta: "Nombre",
            requerido: true
          }
        ]
      }
    ],
    botones: [
      {
        etiqueta: "Crear carpeta",
        callback: crearCarpeta,
        validar: true
      }
    ]
  };

  function crearCarpeta(camposFormulario) {
    setLoaderCrear(true);
    accessAPI(
      "POST",
      "admin/documentocarpeta",
      camposFormulario,
      (respuesta) => {
        setCarpetas((prev) =>
          [...prev, respuesta].sort((a, b) =>
            a.nombre.localeCompare(b.nombre)
          )
        );
        setLoaderCrear(false);
        setSideBarCrear(false);
        toast.current?.show({
          severity: "success",
          summary: "Creada",
          detail: "Carpeta creada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderCrear(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al crear carpeta",
          sticky: true
        });
      }
    );
  }

  function getEditarFormConfig() {
    return {
      secciones: [
        {
          campos: [
            {
              nombre: "nombre",
              tipoitemformularioid: 1,
              etiqueta: "Nombre",
              requerido: true,
              respuesta: editando?.nombre
            }
          ]
        }
      ],
      botones: [
        {
          etiqueta: "Guardar",
          callback: guardarEdicion,
          validar: true
        }
      ]
    };
  }

  function guardarEdicion(camposFormulario) {
    setLoaderEditar(true);
    accessAPI(
      "PUT",
      `admin/documentocarpeta/${editando.id}`,
      camposFormulario,
      () => {
        setCarpetas((prev) =>
          prev
            .map((c) =>
              c.id === editando.id ? { ...c, nombre: camposFormulario.nombre } : c
            )
            .sort((a, b) => a.nombre.localeCompare(b.nombre))
        );
        setLoaderEditar(false);
        setSideBarEditar(false);
        toast.current?.show({
          severity: "success",
          summary: "Actualizada",
          detail: "Carpeta actualizada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        setLoaderEditar(false);
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al actualizar",
          sticky: true
        });
      }
    );
  }

  function eliminarCarpeta(id) {
    accessAPI(
      "DELETE",
      `admin/documentocarpeta/${id}`,
      null,
      () => {
        setCarpetas((prev) => prev.filter((c) => c.id !== id));
        toast.current?.show({
          severity: "success",
          summary: "Eliminada",
          detail: "Carpeta eliminada correctamente",
          life: 3000
        });
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al eliminar",
          sticky: true
        });
      }
    );
  }

  function abrirCrear() {
    setFormCrearKey((prev) => prev + 1);
    setSideBarCrear(true);
  }

  function abrirEditar(carpeta) {
    setEditando(carpeta);
    setFormEditarKey((prev) => prev + 1);
    setSideBarEditar(true);
  }

  const getMenuItems = () => [
    {
      label: "Editar",
      icon: "pi pi-pencil",
      command: () => {
        if (menuSeleccionada) {
          abrirEditar(menuSeleccionada);
        }
      }
    },
    { separator: true },
    {
      label: "Eliminar",
      icon: "pi pi-trash",
      className: "p-menuitem-danger",
      command: () => {
        if (menuSeleccionada) {
          confirmDialog({
            message: `¿Está seguro que desea eliminar "${menuSeleccionada.nombre}"? Los documentos que la tengan asignada quedarán sin carpeta.`,
            header: "Confirmar eliminación",
            icon: "pi pi-exclamation-triangle",
            acceptLabel: "Eliminar",
            rejectLabel: "Cancelar",
            acceptClassName: "p-button-danger",
            accept: () => eliminarCarpeta(menuSeleccionada.id)
          });
        }
      }
    }
  ];

  useEffect(() => {
    document.title = "Carpetas de documentos";
    accessAPI(
      "GET",
      "admin/documentocarpeta/lista",
      null,
      (respuesta) => {
        setCarpetas(respuesta);
        setLoader(false);
      },
      (respuesta) => {
        toast.current?.show({
          severity: "error",
          summary: "Error",
          detail: respuesta[0]?.msg || "Error al cargar carpetas",
          sticky: true
        });
        setLoader(false);
      }
    );
    return () => (document.title = tituloBase());
  }, []);

  return (
    <>
      <Toast ref={toast} position="top-center" />
      <ConfirmDialog />
      <Menu model={getMenuItems()} popup ref={menuRef} />

      <Sidebar
        visible={sideBarCrear}
        onHide={() => setSideBarCrear(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Nueva carpeta</Titulo>
          <Formulario
            key={formCrearKey}
            config={crearFormConfig}
            loader={loaderCrear}
          />
        </div>
      </Sidebar>

      <Sidebar
        visible={sideBarEditar}
        onHide={() => setSideBarEditar(false)}
        position="right"
        closeIcon={
          <div>
            <i className="pi pi-times"></i>
          </div>
        }
        className="w-full lg:w-30rem md:w-30rem"
      >
        <div css={{ marginBottom: "50px" }}>
          <Titulo>Editar carpeta</Titulo>
          <Formulario
            key={formEditarKey}
            config={getEditarFormConfig()}
            loader={loaderEditar}
          />
        </div>
      </Sidebar>

      {loader && (
        <div>
          <div
            css={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "1em",
              marginTop: "2em"
            }}
          >
            <Skeleton width="16rem" height="2rem" />
            <Skeleton width="2.5rem" height="2.5rem" />
          </div>
          <Skeleton width="100%" height="15rem" />
        </div>
      )}

      {!loader && (
        <div>
          <Titulo
            urlVolver="/config"
            botones={
              <Boton callback={abrirCrear}>Nueva carpeta</Boton>
            }
          >
            Carpetas de documentos
          </Titulo>

          <Card>
            <TablaFiltrable
              value={carpetas}
              searchFields={SEARCH_FIELDS}
              anchoBusqueda="33%"
              nombreArchivo="carpetas_documentos"
              size="small"
              emptyMessage="No hay carpetas"
              rowHover
            >
              <Column field="nombre" header="Nombre" />
              <Column
                body={(rowData) => (
                  <Boton
                    icono
                    negro
                    callback={(e) => {
                      setMenuSeleccionada(rowData);
                      menuRef.current.toggle(e);
                    }}
                  >
                    <i className="pi pi-ellipsis-v" />
                  </Boton>
                )}
                style={{ width: "3rem", textAlign: "center" }}
              />
            </TablaFiltrable>
          </Card>
        </div>
      )}
    </>
  );
}
